import cx from 'classnames';
import { XClose } from 'ethena-icons';
import _ from 'lodash';
import useTranslation from 'next-translate/useTranslation';
import {
  createContext,
  ElementType,
  HTMLAttributes,
  useContext,
  useRef,
} from 'react';
import { Modal as BootstrapModal, ModalProps } from 'react-bootstrap';
import css from './Modal.module.scss';

export const MODAL_ANIMATION_DURATION = 150;

/**
 * Context
 */
interface IModalContext {
  handleClose: () => void;
  hasCloseButton: boolean;
}

const ModalContext = createContext<IModalContext>({
  handleClose: _.noop,
  hasCloseButton: true,
});

const useModalContext = () => useContext(ModalContext);

/**
 * Components
 */
export interface IModalProps extends ModalProps {
  hasCloseButton?: boolean;
}

const Modal = ({
  children,
  className,
  hasCloseButton = true,
  onEntering,
  onExit,
  onExited,
  ...rest
}: IModalProps) => {
  const ref = useRef<HTMLDivElement>();

  const handleEntering = (node: HTMLElement, isAppearing: boolean) => {
    if (onEntering) {
      onEntering(node, isAppearing);
    }
  };

  const handleExit = (node: HTMLElement) => {
    if (onExit) {
      onExit(node);
    }
  };

  const handleExited = (node: HTMLElement) => {
    if (onExited) {
      onExited(node);
    }
  };

  return (
    <ModalContext.Provider value={{ handleClose: rest.onHide, hasCloseButton }}>
      <BootstrapModal
        centered
        className={cx(css.modal, className)}
        onEntering={handleEntering}
        onExit={handleExit}
        onExited={handleExited}
        ref={ref}
        {...rest}
      >
        {children}
      </BootstrapModal>
    </ModalContext.Provider>
  );
};

const CloseButton = ({
  className,
  ...rest
}: HTMLAttributes<HTMLButtonElement>) => {
  const { t } = useTranslation('modals');
  const { handleClose } = useModalContext();
  return (
    <button
      aria-label={t('close')}
      className={cx(css.closeButton, className)}
      onClick={handleClose}
      {...rest}
    >
      <XClose aria-hidden="true" height={20} width={20} />
    </button>
  );
};

interface IHeaderProps extends HTMLAttributes<HTMLDivElement> {
  alignment?: 'center' | 'left';
}

const Header = ({
  alignment = 'center',
  children,
  className,
}: IHeaderProps) => {
  const { hasCloseButton } = useModalContext();
  return (
    <BootstrapModal.Header
      className={cx(css.header, className, {
        [css.center]: alignment === 'center',
        [css.left]: alignment === 'left',
      })}
    >
      {children}
      {hasCloseButton && <CloseButton />}
    </BootstrapModal.Header>
  );
};

interface ITitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: ElementType;
}

const Title = ({ as: Component = 'h1', className, ...rest }: ITitleProps) => (
  <Component className={cx(css.title, className)} {...rest} />
);

const Subtitle = ({
  className,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cx(css.subtitle, className)} {...rest} />
);

const Footer = (props: HTMLAttributes<HTMLDivElement>) => (
  <BootstrapModal.Footer as="footer" {...props} />
);

Modal.Body = BootstrapModal.Body;
Modal.CloseButton = CloseButton;
Modal.Dialog = BootstrapModal.Dialog;
Modal.Footer = Footer;
Modal.Header = Header;
Modal.Subtitle = Subtitle;
Modal.Title = Title;

export { Modal };
